import { useState, useEffect, useCallback } from 'react';
import { graphqlClient } from '@/services/core/graphqlClient';
import { useAuth } from '@/contexts/AuthContext';

export interface ProgramStatus {
  program_status_id: number;
  program_status: string;
  created_datetime: string;
}

export interface UseProgramStatusesResult {
  programStatuses: ProgramStatus[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  currentPage: number;
  entriesPerPage: number;
  setCurrentPage: (page: number) => void;
  setEntriesPerPage: (entries: number) => void;
  refetch: () => void;
}

const GET_PROGRAM_STATUSES_QUERY = `
  query GetProgramStatuses($offset: Int!, $limit: Int!) {
    pep_program_status(order_by: {created_datetime: desc}, offset: $offset, limit: $limit) {
      created_datetime
      program_status
      program_status_id
    }
    pep_program_status_aggregate {
      aggregate {
        count
      }
    }
  }
`;

export const useProgramStatuses = (): UseProgramStatusesResult => {
  const { hasuraToken } = useAuth();
  const [statuses, setStatuses] = useState<ProgramStatus[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  const fetchStatuses = useCallback(async () => {
    if (!hasuraToken) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      const offset = (currentPage - 1) * entriesPerPage;
      
      const response = await graphqlClient.query(
        GET_PROGRAM_STATUSES_QUERY,
        { offset, limit: entriesPerPage },
        hasuraToken
      );

      if (response.success && response.data) {
        setStatuses(response.data.pep_program_status || []);
        setTotalCount(response.data.pep_program_status_aggregate?.aggregate?.count || 0);
        setError(null);
      } else {
        setError(response.errors?.[0] || 'Failed to fetch program statuses');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch program statuses');
    } finally {
      setLoading(false);
    }
  }, [hasuraToken, currentPage, entriesPerPage]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  return {
    programStatuses: statuses,
    totalCount,
    loading,
    error,
    currentPage,
    entriesPerPage,
    setCurrentPage,
    setEntriesPerPage: (entries: number) => {
      setEntriesPerPage(entries);
      setCurrentPage(1); // Reset to first page when changing entries per page
    },
    refetch: fetchStatuses
  };
};
